import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import {
  Card,
  Table,
  Select,
  Button,
  Space,
  message,
  Modal,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Divider,
  Spin,
  Upload,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import apiClient from '../../services/apiClient';
import "./AdminPages.css";
import {
  METRIC_COLUMNS,
  formatCurrency,
  formatPercentage,
  buildMetricRows,
  getFieldsByIndustry,
} from '../../constants/financialConstants';

const { Option } = Select;

const INDUSTRY_OPTIONS = [
  { label: "Bank", value: "bank" },
  { label: "NBFC", value: "nbfc" },
  { label: "Manufacturing", value: "manufacturing" },
];

const DEFAULT_NUMERIC_FIELDS = [
  "totalIncome",
  "revenueFromOperations",
  "interestIncome",
  "feesAndCommissionIncome",
  "otherIncome",
  "totalExpenses",
  "operatingExpenses",
  "otherOperatingExpenses",
  "financeCosts",
  "interestExpended",
  "depreciationDepletionAndAmortisationExpense",
  "otherExpenses",
  "impairmentOnFinancialInstruments",
  "employeeBenefitExpenses",
  "operatingProfit",
  "provisionsAndContingencies",
  "provisionsForNpa",
  "profitLossBeforeTax",
  "taxExpenseTotal",
  "profitLossForPeriod",
  "netProfitLossAfterTax",
  "totalComprehensiveIncome",
  "assets",
  "financialAssets",
  "nonFinancialAssets",
  "nonCurrentAssets",
  "currentAssets",
  "investments",
  "advances",
  "otherAssets",
  "equity",
  "capital",
  "reserves",
  "reservesExcludingRevaluation",
  "financialLiabilities",
  "nonFinancialLiabilities",
  "liabilities",
  "nonCurrentLiabilities",
  "currentLiabilities",
  "deposits",
  "borrowings",
  "deferredRevenue",
  "cashFlowsFromUsedInOperatingActivities",
  "cashFlowsFromUsedInInvestingActivities",
  "cashFlowsFromUsedInFinancingActivities",
  "increaseDecreaseInCashAndCashEquivalents",
  "cashAndCashEquivalentsCashFlowStatement",
  "netWorth",
  "operatingMargin",
  "netProfitMargin",
  "debentureRedemptionReserve",
  "debtEquityRatio",
  "debtServiceCoverageRatio",
  "interestServiceCoverageRatio",
  "totalDebtsToTotalAssets",
  "currentRatio",
  "longTermDebtToWorkingCapital",
  "badDebtsToAccountReceivables",
  "currentLiabilityRatio",
  "debtorTurnoverRatio",
  "inventoryTurnoverRatio",
  "basicEarningsPerShare",
  "dilutedEarningsPerShare",
  "earningsPerShareBasic",
  "earningsPerShareDiluted",
  "capitalAdequacyRatio",
  "cet1Ratio",
  "additionalTier1Ratio",
  "grossNpaRatio",
  "netNpaRatio",
  "returnOnAssets",
];


const IssuerFinancialReportsAdmin = () => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080";
  const pocBotsUrl = process.env.REACT_APP_POC_BOTS_URL || "http://localhost:5002";
  const token = localStorage.getItem("token");

  const [issuerOptions, setIssuerOptions] = useState([]);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [selectedPeriodType, setSelectedPeriodType] = useState("annual");

  const [reports, setReports] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  
  // PDF Extraction states
  const [extractionModalVisible, setExtractionModalVisible] = useState(false);
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [extractionError, setExtractionError] = useState(null);
  
  // Comparison modal states
  const [comparisonModalVisible, setComparisonModalVisible] = useState(false);
  const [comparisonRecord, setComparisonRecord] = useState(null);
  const [editableExtractedData, setEditableExtractedData] = useState(null);


  const [form] = Form.useForm();
  const [createForm] = Form.useForm();

  const authHeaders = useMemo(() => {
    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [token]);


useEffect(() => {
  fetchIssuerOptions();
}, []);

  useEffect(() => {
    if (selectedIssuer?.issuer) {
      fetchReports(selectedIssuer.issuer);
    }
  }, [selectedIssuer, selectedPeriodType]);

  const fetchIssuerOptions = async () => {
    try {
      const response = await apiClient.get(
        `${baseUrl}/master-issuer/issuers/basic-industry`,
        { headers: authHeaders }
      );
      if (response.status === 204) {
        setIssuerOptions([]);
        setSelectedIssuer(null);
        return;
      }
      const result = response.data;
      if (result.statusCode === 200 && Array.isArray(result.data)) {
        setIssuerOptions(result.data);
        const defaultIssuer =
            result.data.find((item) => item.issuer === selectedIssuer?.issuer) ||
            result.data[0];
        if (defaultIssuer) {
          setSelectedIssuer(defaultIssuer);
        } else {
          setSelectedIssuer(null);
          setReports([]);
        }
      } else {
        setIssuerOptions([]);
        setSelectedIssuer(null);
      }
    } catch (error) {
      console.error("Error fetching issuer options:", error);
      message.error("Failed to load issuer list");
      setIssuerOptions([]);
      setSelectedIssuer(null);
    }
  };

  const fetchReports = async (issuerName) => {
    if (!issuerName) {
      setReports([]);
      return;
    }
    setTableLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("issuerName", issuerName);
      const response = await apiClient.get(
        `${baseUrl}/issuer-financial-data/issuer?${params.toString()}`,
        { headers: authHeaders }
      );
      if (response.status === 204) {
        setReports([]);
        return;
      }
      const result = response.data;
      if (result.statusCode === 200 && result.data) {
        // Filter reports based on selected period type
        if (selectedPeriodType === "annual" && result.data.annualReports) {
          setReports(result.data.annualReports || []);
        } else if (selectedPeriodType === "quarterly" && result.data.quarterlyReports) {
          setReports(result.data.quarterlyReports || []);
        } else {
          setReports([]);
        }
      } else {
        setReports([]);
      }
    } catch (error) {
      console.error("Error fetching issuer financial reports:", error);
      message.error("Failed to load issuer financial reports");
      setReports([]);
    } finally {
      setTableLoading(false);
    }
  };

  const openViewModal = (record) => {
    // Determine the correct route based on user role
    // Admin users should use /admin/financial-details to stay in admin context with AdminSidebar
    // Internal and External users should use /d/financial-details to use ModernSidebar
    const isAdmin = userData?.roles?.some(role => 
      ['admin', 'super_admin'].includes(role.toLowerCase())
    );
    
    let routePath = "/d/financial-details"; // Default for internal and external users - uses ModernSidebar
    if (isAdmin) {
      routePath = "/admin/financial-details"; // Admin users - uses AdminSidebar
    }
    
    // Navigate to financial details page instead of opening modal
    navigate(routePath, {
      state: {
        data: record,
        issuerName: selectedIssuer?.issuer || record.issuerName || record.id?.companyName,
        basicIndustry: selectedIssuer?.basicIndustry || record.basicIndustry,
        issuer: {
          issuer: selectedIssuer?.issuer || record.issuerName || record.id?.companyName,
          basicIndustry: selectedIssuer?.basicIndustry || record.basicIndustry,
        },
      },
    });
  };

  const openEditModal = (record) => {
    setCurrentRecord(record);
    // Handle both old and new API structures
    const issuerCin = record.issuerCin || selectedIssuer?.corporateIdentityNumber;
    const periodType = record.periodType?.toLowerCase() || (record.periodType?.includes('Year') ? 'annual' : 'quarterly');
    const periodEnd = record.periodEnd || record.endDate;
    const financialYearStart = record.financialYearStart || record.id?.dateOfStartOfFinancialYear;
    const reportingQuarter = record.reportingQuarter || record.id?.reportingQuarter;
    
    // Extract only the financial fields, excluding nested id and metadata
    const { id, createdAt, updatedAt, columnUsed, date, ...financialFields } = record;
    
    form.setFieldsValue({
      ...financialFields,
      issuerCin: issuerCin,
      issuerName: selectedIssuer?.issuer || record.id?.companyName,
      periodType: periodType,
      periodEnd: periodEnd,
      financialYearStart: financialYearStart,
      reportingQuarter: reportingQuarter,
    });
    setEditModalVisible(true);
  };

  const openCreateModal = () => {
    if (!selectedIssuer) {
      message.warning("Please select an issuer before creating a report");
      return;
    }
    createForm.resetFields();
    createForm.setFieldsValue({
      issuerCin: selectedIssuer.corporateIdentityNumber,
      issuerName: selectedIssuer.issuer,
      periodType: selectedPeriodType,
    });
    setCreateModalVisible(true);
  };

  const closeModals = () => {
    setViewModalVisible(false);
    setEditModalVisible(false);
    setCreateModalVisible(false);
    setCurrentRecord(null);
    form.resetFields();
    createForm.resetFields();
  };

  const normalizeNumericFields = (values) => {
    const normalized = { ...values };
    DEFAULT_NUMERIC_FIELDS.forEach((field) => {
      if (field in normalized) {
        const value = normalized[field];
        normalized[field] =
          value === undefined || value === null || value === ""
            ? null
            : Number(value);
      }
    });
    return normalized;
  };

  // Helper function to normalize periodType to "annual" or "quarterly"
  const normalizePeriodType = (record, fallbackType = null) => {
    // First, use the selectedPeriodType from filter (most reliable)
    if (selectedPeriodType && (selectedPeriodType === 'annual' || selectedPeriodType === 'quarterly')) {
      return selectedPeriodType;
    }
    
    // Fallback to fallbackType parameter if provided
    if (fallbackType && (fallbackType === 'annual' || fallbackType === 'quarterly')) {
      return fallbackType;
    }
    
    // Fallback: try to extract from record
    if (record) {
      const recordPeriodType = record.periodType?.toLowerCase() || '';
      if (recordPeriodType.includes('year') || recordPeriodType.includes('annual')) {
        return 'annual';
      } else if (recordPeriodType.includes('quarter') || recordPeriodType.includes('q1') || recordPeriodType.includes('q2') || recordPeriodType.includes('q3') || recordPeriodType.includes('q4')) {
        return 'quarterly';
      }
    }
    
    // Default to annual if nothing works
    return 'annual';
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      
      // Get company name from form or selected issuer
      const companyName = values.issuerName || selectedIssuer?.issuer;
      
      // Normalize periodType to "annual" or "quarterly"
      const periodType = normalizePeriodType(null, values.periodType || selectedPeriodType);
      
      const dateOfStartOfFinancialYear = values.financialYearStart;
      const reportingQuarter = values.reportingQuarter;
      
      if (!companyName || !periodType || !dateOfStartOfFinancialYear || !reportingQuarter) {
        message.error("Missing required fields: company name, period type, financial year start, or reporting quarter");
        return;
      }
      
      // Ensure periodType is exactly "annual" or "quarterly"
      if (periodType !== 'annual' && periodType !== 'quarterly') {
        message.error(`Invalid period type: ${periodType}. Must be 'annual' or 'quarterly'`);
        return;
      }
      
      // Clean up payload - remove identifier fields and metadata that shouldn't be sent
      const financialData = { ...values };
      delete financialData.issuerCin;
      delete financialData.issuerName;
      delete financialData.periodType;
      delete financialData.periodEnd;
      delete financialData.financialYearStart;
      delete financialData.reportingQuarter;
      
      // Normalize numeric fields and prepare payload with id object
      const payload = {
        id: {
          companyName: companyName,
          reportingQuarter: reportingQuarter,
          dateOfStartOfFinancialYear: dateOfStartOfFinancialYear
        },
        periodEnd: values.periodEnd || values.endDate,
        ...normalizeNumericFields(financialData)
      };
      
      const response = await apiClient.post(
        `${baseUrl}/issuer-financial-data/issuer-financial-reports/create/${encodeURIComponent(periodType)}`,
        payload,
        { headers: authHeaders }
      );
      
      const result = response.data;
      if (result.statusCode === 201) {
        message.success("Issuer financial report created successfully");
        closeModals();
        fetchReports(selectedIssuer?.issuer);
      } else {
        message.error(result.message || "Failed to create issuer financial report");
      }
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error("Error creating issuer financial report:", error);
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data ||
        error.message;
      message.error(serverMessage || "Failed to create issuer financial report");
    }
  };

  const handleEdit = async () => {
    if (!currentRecord) return;
    try {
      const values = await form.validateFields();
      
      // Get company name from id or record
      const companyName = currentRecord.id?.companyName || 
                         currentRecord.companyName ||
                         selectedIssuer?.issuer;
      
      // Normalize periodType to "annual" or "quarterly"
      const periodType = normalizePeriodType(currentRecord, values.periodType);
      
      const dateOfStartOfFinancialYear = currentRecord.id?.dateOfStartOfFinancialYear || 
                                        currentRecord.financialYearStart || 
                                        values.financialYearStart;
      const reportingQuarter = currentRecord.id?.reportingQuarter || 
                              currentRecord.reportingQuarter || 
                              values.reportingQuarter;
      
      if (!companyName || !periodType || !dateOfStartOfFinancialYear || !reportingQuarter) {
        message.error("Missing required fields for update");
        return;
      }
      
      // Ensure periodType is exactly "annual" or "quarterly"
      if (periodType !== 'annual' && periodType !== 'quarterly') {
        message.error(`Invalid period type: ${periodType}. Must be 'annual' or 'quarterly'`);
        return;
      }
      
      // Clean up payload - remove identifier fields and metadata that shouldn't be sent
      const financialData = { ...values };
      delete financialData.issuerCin;
      delete financialData.issuerName;
      delete financialData.periodType;
      delete financialData.periodEnd;
      delete financialData.financialYearStart;
      delete financialData.reportingQuarter;
      
      // Normalize numeric fields and prepare payload with id object
      const payload = {
        id: {
          companyName: companyName,
          reportingQuarter: reportingQuarter,
          dateOfStartOfFinancialYear: dateOfStartOfFinancialYear
        },
        ...normalizeNumericFields(financialData)
      };
      
      const response = await apiClient.put(
        `${baseUrl}/issuer-financial-data/issuer-financial-reports/${encodeURIComponent(periodType)}`,
        payload,
        { headers: authHeaders }
      );
      const result = response.data;
      if (result.statusCode === 200) {
        message.success("Issuer financial report updated successfully");
        closeModals();
        fetchReports(selectedIssuer?.issuer);
      } else {
        message.error(result.message || "Failed to update issuer financial report");
      }
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error("Error updating issuer financial report:", error);
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data ||
        error.message;
      message.error(serverMessage || "Failed to update issuer financial report");
    }
  };

  // Helper function to convert snake_case to camelCase
  const snakeToCamel = (str) => {
    return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
  };

  // Helper function to parse date and extract financial year start
  const parseDateAndExtractFY = (dateStr) => {
    if (!dateStr) return { periodEnd: null, financialYearStart: null, reportingQuarter: null };
    
    try {
      // Parse date string like "30 September 2025", "31 March 2025", or "31st March, 2025"
      // Remove ordinal suffixes (st, nd, rd, th) and commas
      let cleanedDate = dateStr.trim()
        .replace(/(\d+)(st|nd|rd|th)/gi, '$1') // Remove ordinal suffixes
        .replace(/,/g, ''); // Remove commas
      
      const dateParts = cleanedDate.split(' ').filter(part => part.trim() !== '');
      
      if (dateParts.length >= 3) {
        const day = parseInt(dateParts[0]);
        const monthStr = dateParts[1];
        const year = parseInt(dateParts[2]);
        
        const monthMap = {
          'january': 1, 'february': 2, 'march': 3, 'april': 4,
          'may': 5, 'june': 6, 'july': 7, 'august': 8,
          'september': 9, 'october': 10, 'november': 11, 'december': 12
        };
        
        const month = monthMap[monthStr.toLowerCase()];
        if (month && !isNaN(day) && !isNaN(year)) {
          const date = new Date(year, month - 1, day);
          const periodEnd = date.toISOString().split('T')[0];
          
          // Financial year start: If month is Jan-Mar, FY starts previous year, else current year
          let fyYear = year;
          if (month <= 3) {
            fyYear = year - 1;
          }
          const financialYearStart = `${fyYear}-04-01`;
          
          // Determine quarter
          let reportingQuarter = null;
          if (month === 6) reportingQuarter = `Q1`;
          else if (month === 9) reportingQuarter = `Q2`;
          else if (month === 12) reportingQuarter = `Q3`;
          else if (month === 3) reportingQuarter = `Q4`;
          else reportingQuarter = `Annual`;
          
          return { periodEnd, financialYearStart, reportingQuarter };
        }
      }
    } catch (error) {
      console.error('Error parsing date:', error);
    }
    
    return { periodEnd: null, financialYearStart: null, reportingQuarter: null };
  };

  // Field name mapping from extracted (snake_case) to API field names (camelCase)
  const fieldMapping = {
    // Income Statement
    'total_income': 'totalIncome',
    'revenue_from_operations': 'revenueFromOperations',
    'interest_income': 'interestIncome',
    'fees_and_commission_income': 'feesAndCommissionIncome',
    'other_income': 'otherIncome',
    'total_expenses': 'totalExpenses',
    'operating_expenses': 'operatingExpenses',
    'other_operating_expenses': 'otherOperatingExpenses',
    'finance_costs': 'financeCosts',
    'interest_expended': 'interestExpended',
    'depreciation_depletion_and_amortisation_expense': 'depreciationDepletionAndAmortisationExpense',
    'other_expenses': 'otherExpenses',
    'impairment_on_financial_instruments': 'impairmentOnFinancialInstruments',
    'employee_benefit_expenses': 'employeeBenefitExpenses',
    'operating_profit': 'operatingProfit',
    'provisions_and_contingencies': 'provisionsAndContingencies',
    'provisions_for_npa': 'provisionsForNpa',
    'profit_loss_before_tax': 'profitLossBeforeTax',
    'profit_loss_for_period': 'profitLossForPeriod',
    'net_profit_loss_after_tax': 'netProfitLossAfterTax',
    'total_comprehensive_income': 'totalComprehensiveIncome',
    'tax_expense_total': 'taxExpenseTotal',
    
    // Assets
    'assets_investments': 'investments',
    'assets_advances': 'advances',
    'assets_other': 'otherAssets',
    'assets': 'assets',
    'financial_assets': 'financialAssets',
    'non_financial_assets': 'nonFinancialAssets',
    'non_current_assets': 'nonCurrentAssets',
    'current_assets': 'currentAssets',
    
    // Liabilities
    'liabilities_capital': 'capital',
    'liabilities_reserves_and_surplus': 'reserves',
    'liabilities_reserves_excluding_revaluation': 'reservesExcludingRevaluation',
    'liabilities_deposits': 'deposits',
    'liabilities_borrowings': 'borrowings',
    'equity': 'equity',
    'financial_liabilities': 'financialLiabilities',
    'non_financial_liabilities': 'nonFinancialLiabilities',
    'liabilities': 'liabilities',
    'deferred_revenue': 'deferredRevenue',
    'borrowings': 'borrowings',
    'non_current_liabilities': 'nonCurrentLiabilities',
    'current_liabilities': 'currentLiabilities',
    
    // Cash Flow
    'cash_flows_from_used_in_operating_activities': 'cashFlowsFromUsedInOperatingActivities',
    'cash_flows_from_used_in_investing_activities': 'cashFlowsFromUsedInInvestingActivities',
    'cash_flows_from_used_in_financing_activities': 'cashFlowsFromUsedInFinancingActivities',
    'cash_and_cash_equivalents_cash_flow_statement': 'cashAndCashEquivalentsCashFlowStatement',
    'increase_decrease_in_cash_and_cash_equivalents': 'increaseDecreaseInCashAndCashEquivalents',
    
    // Ratios & Metrics
    'net_worth': 'netWorth',
    'debenture_redemption_reserve': 'debentureRedemptionReserve',
    'debt_service_coverage_ratio': 'debtServiceCoverageRatio',
    'interest_service_coverage_ratio': 'interestServiceCoverageRatio',
    'debt_equity_ratio': 'debtEquityRatio',
    'current_ratio': 'currentRatio',
    'total_debts_to_total_assets': 'totalDebtsToTotalAssets',
    'long_term_debt_to_working_capital': 'longTermDebtToWorkingCapital',
    'bad_debts_to_account_receivables': 'badDebtsToAccountReceivables',
    'current_liability_ratio': 'currentLiabilityRatio',
    'debtor_turnover_ratio': 'debtorTurnoverRatio',
    'inventory_turnover_ratio': 'inventoryTurnoverRatio',
    'operating_margin': 'operatingMargin',
    'net_profit_margin': 'netProfitMargin',
    'basic_earnings_per_share': 'basicEarningsPerShare',
    'diluted_earnings_per_share': 'dilutedEarningsPerShare',
    'earnings_per_share_basic': 'earningsPerShareBasic',
    'earnings_per_share_diluted': 'earningsPerShareDiluted',
    'capital_adequacy_ratio': 'capitalAdequacyRatio',
    'cet1_ratio': 'cet1Ratio',
    'additional_tier1_ratio': 'additionalTier1Ratio',
    'gross_npa_ratio': 'grossNpaRatio',
    'net_npa_ratio': 'netNpaRatio',
    'return_on_assets': 'returnOnAssets',
  };

  // Helper function to map extracted data to form fields
  const mapExtractedDataToForm = (extractedPeriod) => {
    const formData = {};
    
    // Map fields using the mapping dictionary
    Object.keys(extractedPeriod).forEach((key) => {
      // Skip metadata fields
      if (key === 'period_type' || key === 'end_date' || key === 'column_used' || 
          key === 'quarter' || key === 'end_date_with_quarter' || key === 'announcement_metadata' ||
          key === 'subsidiaries' || key === 'associates' || key === 'joint_ventures') {
        return;
      }
      
      // Get the mapped field name or use snakeToCamel conversion
      const formFieldName = fieldMapping[key] || snakeToCamel(key);
      let value = extractedPeriod[key];
      
      // Skip if value is "Not applicable" or "N/A"
      if (value === 'Not applicable' || value === 'N/A' || value === null || value === undefined) {
        return;
      }
      
      // Convert numeric strings to numbers (remove commas, handle percentages, handle negative values in parentheses)
      if (value && typeof value === 'string' && value.trim() !== '') {
        // Handle negative values in parentheses like "(350.04)"
        let isNegative = false;
        if (value.trim().startsWith('(') && value.trim().endsWith(')')) {
          isNegative = true;
          value = value.replace(/[()]/g, '').trim();
        }
        
        // Remove percentage sign for percentage fields
        const hasPercentage = value.includes('%');
        value = value.replace(/%/g, '').trim();
        
        // Try to parse as number (remove commas and spaces)
        const cleaned = value.replace(/,/g, '').replace(/\s/g, '').trim();
        if (cleaned !== '' && !isNaN(cleaned)) {
          let numValue = parseFloat(cleaned);
          if (isNegative) {
            numValue = -Math.abs(numValue);
          }
          value = numValue;
        } else if (hasPercentage) {
          // If it had a percentage but couldn't parse, keep original string
          value = extractedPeriod[key];
        }
      }
      
      // Only add non-null/undefined values
      if (value !== null && value !== undefined && value !== '') {
        formData[formFieldName] = value;
      }
    });
    
    return formData;
  };

  const handleUploadAndExtractPDF = () => {
    if (!selectedIssuer) {
      message.warning("Please select an issuer before uploading PDF");
      return;
    }

    // Create a file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Open modal first and show loading
      createForm.resetFields();
      createForm.setFieldsValue({
        issuerCin: selectedIssuer.corporateIdentityNumber,
        issuerName: selectedIssuer.issuer,
        periodType: selectedPeriodType,
      });
      setCreateModalVisible(true);
      setExtractionLoading(true);

      try {
        // Create FormData to send file
        const formData = new FormData();
        formData.append('file', file);
        
        // Pass basicIndustry as type parameter
        if (selectedIssuer?.basicIndustry) {
          formData.append('type', selectedIssuer.basicIndustry);
        }
        
        // Determine period type filter based on selected period type
        const periodTypeFilter = selectedPeriodType === 'quarterly' ? 'quarter' : 
                                selectedPeriodType === 'annual' ? 'annual' : '';
        if (periodTypeFilter) {
          formData.append('period_type', periodTypeFilter);
        }

        // Call extraction API with file upload
        const response = await fetch(`${pocBotsUrl}/api/extract-financial-data`, {
          method: 'POST',
          body: formData,
        });

        const result = await response.json();

        if (result.status === 'success' && result.data && result.data.length > 0) {
          // Use the first period data (or let user select if multiple)
          const extractedPeriod = result.data[0];
          
          // Parse date information from end_date
          const dateInfo = parseDateAndExtractFY(extractedPeriod.end_date);
          
          // Use quarter from extracted data if available
          let reportingQuarter = dateInfo.reportingQuarter;
          if (extractedPeriod.quarter && !reportingQuarter) {
            // Extract year from end_date for quarter format
            const yearMatch = extractedPeriod.end_date?.match(/\d{4}/);
            const year = yearMatch ? yearMatch[0].slice(-2) : new Date().getFullYear().toString().slice(-2);
            reportingQuarter = `${extractedPeriod.quarter} FY${year}`;
          }
          
          // Map extracted data to form fields
          const mappedData = mapExtractedDataToForm(extractedPeriod);
          
          // Prepare form values
          const formValues = {
            issuerCin: selectedIssuer.corporateIdentityNumber,
            issuerName: selectedIssuer.issuer,
            periodType: selectedPeriodType,
            periodEnd: dateInfo.periodEnd || extractedPeriod.end_date, // Use parsed date or fallback to original
            financialYearStart: dateInfo.financialYearStart,
            reportingQuarter: reportingQuarter || (selectedPeriodType === 'annual' ? `Annual FY${new Date().getFullYear().toString().slice(-2)}` : 'Q1'),
            ...mappedData
          };
          
          // Populate form with extracted data
          createForm.setFieldsValue(formValues);
          
          // Store extracted data for reference
          setExtractedData(result);
          
          message.success({ 
            content: `Successfully extracted ${result.data.length} period(s). Form populated with data.`, 
            key: 'extract',
            duration: 3
          });
        } else {
          message.error({ 
            content: result.message || 'Failed to extract financial data', 
            key: 'extract',
            duration: 3
          });
          setExtractionError(result.message || 'Failed to extract financial data');
        }
      } catch (error) {
        message.error({ 
          content: 'Error extracting financial data: ' + error.message, 
          key: 'extract',
          duration: 3
        });
        setExtractionError(error.message || 'An error occurred while extracting data');
      } finally {
        setExtractionLoading(false);
      }
    };
    input.click();
  };

  const handleExtractFromPDF = (record = null) => {
    // Create a file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // If record is provided, show comparison modal
      if (record) {
        setComparisonRecord(record);
        setComparisonModalVisible(true);
        setExtractionLoading(true);
      } else {
        // Show loader and modal
        setExtractionLoading(true);
        setExtractionModalVisible(true);
        setExtractedData(null);
        setExtractionError(null);
      }

      try {
        // Create FormData to send file
        const formData = new FormData();
        formData.append('file', file);
        
        // Pass basicIndustry as type parameter (use selectedIssuer or record's basicIndustry)
        const basicIndustry = selectedIssuer?.basicIndustry || record?.basicIndustry || 'manufacturing';
        formData.append('type', basicIndustry);
        
        // Determine period type filter based on selected period type
        const periodTypeFilter = selectedPeriodType === 'quarterly' ? 'quarter' : 
                                selectedPeriodType === 'annual' ? 'annual' : '';
        if (periodTypeFilter) {
          formData.append('period_type', periodTypeFilter);
        }

        // Call extraction API with file upload
        const response = await fetch(`${pocBotsUrl}/api/extract-financial-data`, {
          method: 'POST',
          body: formData,
        });

        const result = await response.json();

        if (result.status === 'success' && result.data && result.data.length > 0) {
          const extractedPeriod = result.data[0];
          const mappedData = mapExtractedDataToForm(extractedPeriod);
          
          if (record) {
            // Set editable extracted data for comparison
            setEditableExtractedData(mappedData);
            message.success(`Successfully extracted ${result.data.length} period(s) of financial data`);
          } else {
            setExtractedData(result);
            message.success(`Successfully extracted ${result.data.length} period(s) of financial data`);
          }
        } else {
          setExtractionError(result.message || 'Failed to extract financial data');
          message.error(result.message || 'Failed to extract financial data');
          if (record) {
            setComparisonModalVisible(false);
          }
        }
      } catch (error) {
        setExtractionError(error.message || 'An error occurred while extracting data');
        message.error('Error extracting financial data: ' + error.message);
        if (record) {
          setComparisonModalVisible(false);
        }
      } finally {
        setExtractionLoading(false);
      }
    };
    input.click();
  };
  
  const handleSaveComparison = async () => {
    if (!comparisonRecord || !editableExtractedData) return;
    
    try {
      // Get company name from id or record
      const companyName = comparisonRecord.id?.companyName || 
                         comparisonRecord.companyName ||
                         selectedIssuer?.issuer;
      
      // Normalize periodType to "annual" or "quarterly"
      const periodType = normalizePeriodType(comparisonRecord);
      
      const dateOfStartOfFinancialYear = comparisonRecord.id?.dateOfStartOfFinancialYear || 
                                        comparisonRecord.financialYearStart;
      const reportingQuarter = comparisonRecord.id?.reportingQuarter || 
                              comparisonRecord.reportingQuarter;
      
      if (!companyName || !periodType || !dateOfStartOfFinancialYear || !reportingQuarter) {
        message.error("Missing required fields for update");
        return;
      }
      
      // Ensure periodType is exactly "annual" or "quarterly"
      if (periodType !== 'annual' && periodType !== 'quarterly') {
        message.error(`Invalid period type: ${periodType}. Must be 'annual' or 'quarterly'`);
        return;
      }
      
      // Prepare payload with id object and extracted data
      const payload = {
        id: {
          companyName: companyName,
          reportingQuarter: reportingQuarter,
          dateOfStartOfFinancialYear: dateOfStartOfFinancialYear
        },
        ...normalizeNumericFields(editableExtractedData)
      };
      
      const response = await apiClient.put(
        `${baseUrl}/issuer-financial-data/issuer-financial-reports/${encodeURIComponent(periodType)}`,
        payload,
        { headers: authHeaders }
      );
      
      const result = response.data;
      if (result.statusCode === 200) {
        message.success("Issuer financial report updated successfully");
        setComparisonModalVisible(false);
        setComparisonRecord(null);
        setEditableExtractedData(null);
        fetchReports(selectedIssuer?.issuer);
      } else {
        message.error(result.message || "Failed to update issuer financial report");
      }
    } catch (error) {
      console.error("Error updating issuer financial report:", error);
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data ||
        error.message;
      message.error(serverMessage || "Failed to update issuer financial report");
    }
  };
  
  const handleUpdateExtractedField = (fieldKey, value) => {
    setEditableExtractedData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Optimized helper function to compare two values (handles null, undefined, numbers, strings)
  const areValuesDifferent = (val1, val2) => {
    // Both are null/undefined - consider same
    if ((val1 === null || val1 === undefined) && (val2 === null || val2 === undefined)) {
      return false;
    }
    
    // One is null/undefined and other is not - different
    if ((val1 === null || val1 === undefined) !== (val2 === null || val2 === undefined)) {
      return true;
    }
    
    // Both are numbers - compare with tolerance for floating point precision
    if (typeof val1 === 'number' && typeof val2 === 'number') {
      return Math.abs(val1 - val2) > 0.01; // Tolerance for rounding differences
    }
    
    // Convert both to numbers if possible for numeric comparison
    const num1 = typeof val1 === 'number' ? val1 : (val1 !== null && val1 !== undefined && !isNaN(Number(val1)) ? Number(val1) : null);
    const num2 = typeof val2 === 'number' ? val2 : (val2 !== null && val2 !== undefined && !isNaN(Number(val2)) ? Number(val2) : null);
    
    if (num1 !== null && num2 !== null) {
      return Math.abs(num1 - num2) > 0.01;
    }
    
    // String comparison
    return String(val1) !== String(val2);
  };
  
  const renderComparisonSection = (title, fields) => {
    if (!comparisonRecord || !editableExtractedData) return null;
    
    // Compute table data with difference detection
    const tableData = fields.map(field => {
      const currentValue = comparisonRecord[field.key] != null ? comparisonRecord[field.key] : null;
      const extractedValue = editableExtractedData[field.key] != null ? editableExtractedData[field.key] : null;
      const isDifferent = areValuesDifferent(currentValue, extractedValue);
      
      return {
        key: field.key,
        field: field.label,
        fieldKey: field.key,
        current: currentValue,
        extracted: extractedValue,
        isDifferent,
      };
    });
    
    return (
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ marginBottom: 16 }}>{title}</h4>
        <Table
          className="issuer-financial-reports-table"
          dataSource={tableData}
          rowClassName={(record) => {
            // Highlight row in red if values are different
            return record.isDifferent ? 'comparison-row-different' : '';
          }}
          columns={[
            {
              title: 'Field',
              dataIndex: 'field',
              key: 'field',
              width: '30%',
            },
            {
              title: 'Current Data',
              dataIndex: 'current',
              key: 'current',
              width: '20%',
              render: (value) => {
                if (value === null || value === undefined) {
                  return <span style={{ color: '#999' }}>-</span>;
                }
                return (
                  <span style={{ 
                    padding: '4px 8px', 
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}>
                    {typeof value === 'number' ? value.toLocaleString() : value}
                  </span>
                );
              },
            },
            {
              title: 'Extracted Data (Editable)',
              dataIndex: 'extracted',
              key: 'extracted',
              width: '50%',
              render: (value, record) => {
                const fieldKey = record.fieldKey;
                
                return (
                  <InputNumber
                    value={value}
                    onChange={(newValue) => {
                      handleUpdateExtractedField(fieldKey, newValue === null ? null : newValue);
                    }}
                    placeholder="Enter value"
                    style={{ width: '100%' }}
                    formatter={(value) => {
                      if (value === null || value === undefined) return '';
                      return typeof value === 'number' ? value.toLocaleString() : String(value);
                    }}
                    parser={(value) => {
                      if (!value) return null;
                      const parsed = value.replace(/,/g, '');
                      return parsed === '' ? null : parseFloat(parsed);
                    }}
                    precision={2}
                  />
                );
              },
            },
          ]}
          pagination={false}
          size="small"
        />
      </div>
    );
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete issuer financial report",
      content: "Are you sure you want to delete this report?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          // Get company name from id or record
          const companyName = record.id?.companyName || 
                             record.companyName ||
                             selectedIssuer?.issuer;
          
          // Normalize periodType to "annual" or "quarterly"
          const periodType = normalizePeriodType(record);
          
          const dateOfStartOfFinancialYear = record.id?.dateOfStartOfFinancialYear || 
                                            record.financialYearStart;
          const reportingQuarter = record.id?.reportingQuarter || 
                                 record.reportingQuarter;
          
          if (!companyName || !periodType || !dateOfStartOfFinancialYear || !reportingQuarter) {
            message.error("Missing required fields for deletion");
            return;
          }
          
          // Ensure periodType is exactly "annual" or "quarterly"
          if (periodType !== 'annual' && periodType !== 'quarterly') {
            message.error(`Invalid period type: ${periodType}. Must be 'annual' or 'quarterly'`);
            return;
          }
          
          // Prepare request body with id object
          const requestBody = {
            id: {
              companyName: companyName,
              reportingQuarter: reportingQuarter,
              dateOfStartOfFinancialYear: dateOfStartOfFinancialYear
            }
          };
          
          const response = await apiClient.delete(
            `${baseUrl}/issuer-financial-data/issuer-financial-reports/${encodeURIComponent(periodType)}`,
            {
              headers: authHeaders,
              data: requestBody,
            }
          );
          const result = response.data;
          if (result.statusCode === 200) {
            message.success("Issuer financial report deleted successfully");
            fetchReports(selectedIssuer?.issuer);
          } else {
            message.error(result.message || "Failed to delete issuer financial report");
          }
        } catch (error) {
          console.error("Error deleting issuer financial report:", error);
          const serverMessage =
            error.response?.data?.message ||
            error.response?.data ||
            error.message;
          message.error(serverMessage || "Failed to delete issuer financial report");
        }
      },
    });
  };


  const columns = [
    {
      title: "Period End",
      dataIndex: "endDate",
      key: "endDate",
      sorter: (a, b) => {
        const aDate = a.endDate || "";
        const bDate = b.endDate || "";
        return aDate.localeCompare(bDate);
      },
    },
    {
      title: "Financial Year Start",
      dataIndex: ["id", "dateOfStartOfFinancialYear"],
      key: "financialYearStart",
      sorter: (a, b) => {
        const aDate = a.id?.dateOfStartOfFinancialYear || "";
        const bDate = b.id?.dateOfStartOfFinancialYear || "";
        return aDate.localeCompare(bDate);
      },
    },
    {
      title: "Reporting Quarter",
      dataIndex: ["id", "reportingQuarter"],
      key: "reportingQuarter",
    },
    {
      title: "Period Type",
      dataIndex: "periodType",
      key: "periodType",
      render: (value) => (value ? value.toUpperCase() : "-"),
    },
    {
      title: "Revenue",
      dataIndex: "revenueFromOperations",
      key: "revenueFromOperations",
      render: (value) => (value != null ? value.toLocaleString() : "-"),
    },
    {
      title: "Profit After Tax",
      dataIndex: "profitLossForPeriod",
      key: "profitLossForPeriod",
      render: (value) => (value != null ? value.toLocaleString() : "-"),
    },
    {
      title: "Total Income",
      dataIndex: "totalIncome",
      key: "totalIncome",
      render: (value) => (value != null ? value.toLocaleString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => openViewModal(record)}
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => handleExtractFromPDF(record)}
            title="Extract Financial Data from PDF"
          />
        </Space>
      ),
    },
  ];

  const renderViewModal = () => {
    if (!currentRecord) return null;

    // Get industry-specific fields based on selected issuer's basicIndustry and record's periodType
    const basicIndustry = selectedIssuer?.basicIndustry || currentRecord.basicIndustry;
    const periodType = currentRecord.periodType || selectedPeriodType;
    const fields = getFieldsByIndustry(basicIndustry, periodType);

    const incomeData = buildMetricRows(currentRecord, fields.income);
    const assetData = buildMetricRows(currentRecord, fields.assets);
    const liabilityData = buildMetricRows(currentRecord, fields.liabilities);
    const cashFlowData = buildMetricRows(currentRecord, fields.cashFlow);
    const keyMetricData = buildMetricRows(currentRecord, fields.keyMetrics);

    const renderMetricCard = (title, data, span = 12) => (
      <Col span={span} style={{ marginBottom: 16 }}>
        <Card size="small" title={title}>
          <Table
            size="small"
            columns={METRIC_COLUMNS}
            dataSource={data}
            pagination={false}
            rowKey="item"
          />
        </Card>
      </Col>
    );

    return (
      <Modal
        open={viewModalVisible}
        onCancel={closeModals}
        footer={null}
        title="Issuer Financial Report Details"
        width={1000}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Issuer">
            {currentRecord.issuerName || selectedIssuer?.issuer}
          </Descriptions.Item>
          <Descriptions.Item label="Issuer CIN">
            {currentRecord.issuerCin || selectedIssuer?.corporateIdentityNumber}
          </Descriptions.Item>
          <Descriptions.Item label="Period Type">
            {currentRecord.periodType || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Period End">
            {currentRecord.periodEnd || currentRecord.endDate || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Financial Year Start">
            {currentRecord.financialYearStart || currentRecord.id?.dateOfStartOfFinancialYear || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Reporting Quarter">
            {currentRecord.reportingQuarter || currentRecord.id?.reportingQuarter || "-"}
          </Descriptions.Item>
        </Descriptions>

        <Divider />
        <Row gutter={16}>
          {renderMetricCard("Income Statement", incomeData)}
          {fields.cashFlow.length > 0 && renderMetricCard("Cash Flow", cashFlowData)}
        </Row>
        {fields.assets.length > 0 && fields.liabilities.length > 0 && (
        <Row gutter={16}>
          {renderMetricCard("Assets", assetData)}
          {renderMetricCard("Liabilities", liabilityData)}
        </Row>
        )}

        <Divider orientation="left">Key Metrics</Divider>
        <Descriptions bordered column={2} size="small">
          {keyMetricData.map((metric) => (
            <Descriptions.Item key={metric.key} label={metric.item}>
              {metric.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Modal>
    );
  };

  const renderNumericSection = (title, fields) => (
    <>
      <Divider orientation="left">{title}</Divider>
      <Row gutter={16}>
        {fields.map(({ key, label }) => (
          <Col span={8} key={key}>
            <Form.Item name={key} label={label}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        ))}
      </Row>
    </>
  );

  const renderFormFields = (isCreate = false) => {
    // Get industry-specific fields based on selected issuer's basicIndustry and period type
    const basicIndustry = selectedIssuer?.basicIndustry;
    
    // For create modal, always use selectedPeriodType
    // For edit modal, use the record's period type or form value
    let periodType;
    if (isCreate) {
      // Use selectedPeriodType from the filter, or get from form if user changed it
      const formPeriodType = createForm?.getFieldValue('periodType');
      periodType = formPeriodType || selectedPeriodType;
    } else {
      periodType = currentRecord?.periodType?.toLowerCase() || 
         (currentRecord?.periodType?.includes('Year') ? 'annual' : 'quarterly') ||
         form.getFieldValue('periodType') || 
         selectedPeriodType;
    }
    
    // Normalize period type to ensure it's 'annual' or 'quarterly'
    if (periodType) {
      const periodLower = periodType.toLowerCase();
      if (periodLower.includes('annual') || periodLower.includes('year')) {
        periodType = 'annual';
      } else if (periodLower.includes('quarter') || periodLower.includes('q')) {
        periodType = 'quarterly';
      }
    }
    
    const fields = getFieldsByIndustry(basicIndustry, periodType);

    return (
    <>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="issuerCin"
            label="Issuer CIN"
            rules={[{ required: true, message: "Issuer CIN is required" }]}
          >
            <Input disabled />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="issuerName"
            label="Issuer Name"
            rules={[{ required: true, message: "Issuer name is required" }]}
          >
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="periodType"
            label="Period Type"
            rules={[{ required: true, message: "Period type is required" }]}
          >
              <Select 
                disabled={!isCreate}
                onChange={(value) => {
                  // When period type changes, update form fields dynamically
                  if (isCreate) {
                    createForm.setFieldsValue({ periodType: value });
                  } else {
                    form.setFieldsValue({ periodType: value });
                  }
                }}
              >
              <Option value="annual">Annual</Option>
              <Option value="quarterly">Quarterly</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="periodEnd"
            label="Period End"
            rules={[{ required: true, message: "Period end is required" }]}
          >
              <Input placeholder="YYYY-MM-DD" disabled={!isCreate} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="financialYearStart"
            label="Financial Year Start"
            rules={[{ required: true, message: "Financial year start is required" }]}
          >
            <Input placeholder="YYYY-MM-DD" disabled={!isCreate} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="reportingQuarter"
            label="Reporting Quarter"
            rules={[{ required: true, message: "Reporting quarter is required" }]}
          >
            <Input placeholder="e.g. Q1 FY24" disabled={!isCreate} />
          </Form.Item>
        </Col>
      </Row>
        {fields.income.length > 0 && renderNumericSection("Income Statement", fields.income)}
        {fields.assets.length > 0 && renderNumericSection("Assets", fields.assets)}
        {fields.liabilities.length > 0 && renderNumericSection("Liabilities", fields.liabilities)}
        {fields.cashFlow.length > 0 && renderNumericSection("Cash Flow", fields.cashFlow)}
        {fields.keyMetrics.length > 0 && renderNumericSection("Ratios & Performance", fields.keyMetrics)}
    </>
  );
  };

  return (
    <div className="admin-page">
      <div className="admin-header-banner">
        <div className="hero-content">
          <h1 className="hero-title-news">Issuer Financial Reports</h1>
         
        </div>
      </div>

      <Card className="admin-main-card">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="bottom">
          <Col xs={24} md={10}>
            <label className="admin-filter-label">Issuer</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select issuer"
              value={selectedIssuer?.issuer}
              onChange={(value) => {
                const issuer = issuerOptions.find((item) => item.issuer === value);
                setSelectedIssuer(issuer || null);
              }}
              loading={tableLoading && issuerOptions.length === 0}
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {issuerOptions.map((issuer) => (
                <Option key={issuer.issuer} value={issuer.issuer}>
                  {issuer.issuer}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <label className="admin-filter-label">Period Type</label>
            <Select
              style={{ width: "100%" }}
              value={selectedPeriodType}
              onChange={setSelectedPeriodType}
            >
              <Option value="annual">Annual</Option>
              <Option value="quarterly">Quarterly</Option>
            </Select>
          </Col>
          <Col
            xs={24}
            md={8}
            style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleUploadAndExtractPDF}
              disabled={!selectedIssuer}
              loading={extractionLoading}
            >
              {selectedPeriodType === "annual" ? "Upload Annual PDF" : "Upload Quarterly PDF"}
            </Button>
          </Col>
        </Row>

        <Table
          className="issuer-financial-reports-table"
          columns={columns}
          dataSource={reports}
          rowKey={(record) =>
            `${record.id?.companyName || ''}-${record.periodType || ''}-${record.id?.dateOfStartOfFinancialYear || ''}-${record.id?.reportingQuarter || ''}`
          }
          loading={tableLoading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        open={createModalVisible}
        onCancel={closeModals}
        onOk={handleCreate}
        title={`Create ${selectedPeriodType === 'annual' ? 'Annual' : 'Quarterly'} Financial Report`}
        width={800}
        okText="Create"
        confirmLoading={extractionLoading}
      >
        <Spin spinning={extractionLoading} tip="Extracting data from PDF...">
          <Form 
            layout="vertical" 
            form={createForm}
            initialValues={{
              periodType: selectedPeriodType
            }}
          >
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.periodType !== currentValues.periodType
              }
            >
              {() => renderFormFields(true)}
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      <Modal
        open={editModalVisible}
        onCancel={closeModals}
        onOk={handleEdit}
        title="Edit Issuer Financial Report"
        width={800}
        okText="Save Changes"
      >
        <Form layout="vertical" form={form}>
          {renderFormFields()}
        </Form>
      </Modal>

      {renderViewModal()}

      {/* Comparison Modal - Current vs Extracted Data */}
      <Modal
        open={comparisonModalVisible}
        onCancel={() => {
          setComparisonModalVisible(false);
          setComparisonRecord(null);
          setEditableExtractedData(null);
          setExtractionLoading(false);
        }}
        onOk={handleSaveComparison}
        title="Compare & Update Financial Data"
        width={1400}
        okText="Save Changes"
        cancelText="Cancel"
        footer={
          !extractionLoading && editableExtractedData ? (
            [
              <Button
                key="cancel"
                onClick={() => {
                  setComparisonModalVisible(false);
                  setComparisonRecord(null);
                  setEditableExtractedData(null);
                  setExtractionLoading(false);
                }}
              >
                Cancel
              </Button>,
              <Button
                key="save"
                type="primary"
                onClick={handleSaveComparison}
              >
                Save Changes
              </Button>,
            ]
          ) : null
        }
      >
        <Spin spinning={extractionLoading} tip="Extracting data from PDF...">
          {comparisonRecord && editableExtractedData && !extractionLoading && (() => {
            // Get industry-specific fields based on selected issuer's basicIndustry and record's periodType
            const basicIndustry = selectedIssuer?.basicIndustry || comparisonRecord.basicIndustry;
            const periodType = comparisonRecord.periodType || selectedPeriodType;
            const fields = getFieldsByIndustry(basicIndustry, periodType);

            return (
            <div>
              <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                <Row gutter={16}>
                  <Col span={8}>
                      <strong>Period End:</strong> {comparisonRecord.periodEnd || comparisonRecord.endDate || "-"}
                  </Col>
                  <Col span={8}>
                      <strong>Financial Year Start:</strong> {comparisonRecord.financialYearStart || comparisonRecord.id?.dateOfStartOfFinancialYear || "-"}
                  </Col>
                  <Col span={8}>
                      <strong>Reporting Quarter:</strong> {comparisonRecord.reportingQuarter || comparisonRecord.id?.reportingQuarter || "-"}
                  </Col>
                </Row>
              </div>
              
                {fields.income.length > 0 && renderComparisonSection("Income Statement", fields.income)}
                {fields.assets.length > 0 && renderComparisonSection("Assets", fields.assets)}
                {fields.liabilities.length > 0 && renderComparisonSection("Liabilities", fields.liabilities)}
                {fields.cashFlow.length > 0 && renderComparisonSection("Cash Flow", fields.cashFlow)}
                {fields.keyMetrics.length > 0 && renderComparisonSection("Ratios & Performance", fields.keyMetrics)}
            </div>
            );
          })()}
        </Spin>
      </Modal>

      {/* PDF Extraction Modal */}
      <Modal
        open={extractionModalVisible}
        onCancel={() => {
          setExtractionModalVisible(false);
          setExtractedData(null);
          setExtractionError(null);
        }}
        title="Extracted Financial Data from PDF"
        width={1200}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setExtractionModalVisible(false);
              setExtractedData(null);
              setExtractionError(null);
            }}
          >
            Close
          </Button>,
        ]}
      >
        {extractionLoading ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Extracting financial data from PDF...</p>
            <p style={{ color: "#999", fontSize: "12px" }}>This may take a few moments</p>
          </div>
        ) : extractionError ? (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <p style={{ color: "#ff4d4f", fontSize: "16px", marginBottom: "1rem" }}>
              Error: {extractionError}
            </p>
          </div>
        ) : extractedData ? (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Status">{extractedData.status}</Descriptions.Item>
              <Descriptions.Item label="Type">{extractedData.type}</Descriptions.Item>
              <Descriptions.Item label="Period Filter">
                {extractedData.period_type_filter || "All"}
              </Descriptions.Item>
              <Descriptions.Item label="Periods Extracted">
                {extractedData.data?.length || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Message" span={2}>
                {extractedData.message}
              </Descriptions.Item>
            </Descriptions>

            {extractedData.data && extractedData.data.length > 0 ? (
              <div>
                <h3 style={{ marginTop: 24, marginBottom: 16 }}>Extracted Financial Data:</h3>
                {extractedData.data.map((periodData, index) => (
                  <Card
                    key={index}
                    title={`Period ${index + 1}: ${periodData.period_type || "N/A"} - ${periodData.end_date || "N/A"}`}
                    style={{ marginBottom: 16 }}
                  >
                    <Descriptions bordered column={2}>
                      <Descriptions.Item label="Period Type">
                        {periodData.period_type || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="End Date">
                        {periodData.end_date || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Column Used" span={2}>
                        {periodData.column_used || "N/A"}
                      </Descriptions.Item>
                      {periodData.quarter && (
                        <Descriptions.Item label="Quarter">
                          {periodData.quarter}
                        </Descriptions.Item>
                      )}
                    </Descriptions>

                    <Divider>Financial Metrics</Divider>
                    
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Table
                          size="small"
                          dataSource={Object.entries(periodData)
                            .filter(([key]) => 
                              !['period_type', 'end_date', 'column_used', 'quarter', 'end_date_with_quarter', 'announcement_metadata'].includes(key)
                            )
                            .map(([key, value]) => ({
                              key: key,
                              metric: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                              value: value || "N/A"
                            }))}
                          columns={[
                            {
                              title: "Metric",
                              dataIndex: "metric",
                              key: "metric",
                              width: "40%",
                            },
                            {
                              title: "Value",
                              dataIndex: "value",
                              key: "value",
                              width: "60%",
                            },
                          ]}
                          pagination={false}
                          rowKey="key"
                        />
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            ) : (
              <div style={{ padding: "2rem", textAlign: "center", color: "#999" }}>
                No financial data extracted
              </div>
            )}

            <Divider />
            <div style={{ marginTop: 16 }}>
              <h4>Raw JSON Response:</h4>
              <pre
                style={{
                  background: "#f5f5f5",
                  padding: "16px",
                  borderRadius: "4px",
                  overflow: "auto",
                  maxHeight: "400px",
                  fontSize: "12px",
                }}
              >
                {JSON.stringify(extractedData, null, 2)}
              </pre>
            </div>
          </div>
        ) : (
          <div style={{ padding: "2rem", textAlign: "center", color: "#999" }}>
            No data to display
          </div>
        )}
      </Modal>

      {tableLoading && reports.length === 0 && (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <Spin size="large" />
        </div>
      )}
    </div>
  );
};

export default IssuerFinancialReportsAdmin;

